import { useCallback, useContext, useEffect } from "react";

import { MapContext } from "fond/map/MapProvider";

const SOURCE_ID = "multiProject-source";

interface SelectedSubareaHighlightProps {
  selectedIds: string[];
  hoveredAreaId?: string | null;
  /**
   * IDs of subareas that already have a project created for them.
   * These will be rendered with a disabled appearance on the map.
   */
  projectAreaIds?: string[];
}

/**
 * Renderless component that highlights subareas on the map by setting
 * feature-state (isHighlighted / isHovered / hasProject) on the multiProject source.
 *
 * Listens for `sourcedata` events to re-apply states when the source is
 * recreated (e.g. when the legend categorization changes).
 */
const SelectedSubareaHighlight: React.FC<SelectedSubareaHighlightProps> = ({
  selectedIds,
  hoveredAreaId,
  projectAreaIds,
}: SelectedSubareaHighlightProps) => {
  const { map } = useContext(MapContext);

  const setFeatureState = useCallback(
    (id: string, state: Record<string, unknown>) => {
      if (!map?.getSource(SOURCE_ID)) return;
      map.setFeatureState({ source: SOURCE_ID, id }, state);
    },
    [map]
  );

  // Manage isHighlighted and hasProject states.
  // Re-applies via sourcedata listener to survive source recreation (e.g. categorization changes).
  useEffect(() => {
    if (!map) return undefined;

    const apply = () => {
      if (!map.getSource(SOURCE_ID)) return;
      selectedIds.forEach((id) => setFeatureState(id, { isHighlighted: true }));
      projectAreaIds?.forEach((id) => setFeatureState(id, { hasProject: true }));
    };

    apply();

    const handleSourceData = (e: { sourceId: string }) => {
      if (e.sourceId === SOURCE_ID) {
        apply();
      }
    };

    map.on("sourcedata", handleSourceData);

    return () => {
      map.off("sourcedata", handleSourceData);
      selectedIds.forEach((id) => setFeatureState(id, { isHighlighted: false }));
      projectAreaIds?.forEach((id) => setFeatureState(id, { hasProject: false }));
    };
  }, [map, selectedIds, projectAreaIds, setFeatureState]);

  // Manage isHovered state separately — changes frequently and doesn't need sourcedata re-application
  useEffect(() => {
    if (hoveredAreaId) {
      setFeatureState(hoveredAreaId, { isHovered: true });
    }

    return () => {
      if (hoveredAreaId) {
        setFeatureState(hoveredAreaId, { isHovered: false });
      }
    };
  }, [hoveredAreaId, setFeatureState]);

  return null;
};

export default SelectedSubareaHighlight;
